import React, { useState } from "react";
import { BookOpen, Volume2, VolumeX, Glasses, AlertTriangle, Sliders, Menu } from "lucide-react";

export default function SurvivalHUD({
  countdownText,
  historicalText,
  decay,
  muted,
  onToggleMute,
  onFactCheck,
  onEnterVR,
  vrSupported,
  phaseTitle,
  studyMode,
  volume,
  onVolume,
  onMenu,
}) {
  const [mixerOpen, setMixerOpen] = useState(false);
  return (
    <div
      className="hud-topbar"
      style={{
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "space-between",
        padding: "12px 18px",
        pointerEvents: "auto",
      }}
    >
      {/* LEFT quadrant — TAB seal + title, pinned top-left (no movement) */}
      <div className="hud-left" style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0 }}>
        <img
          src="/brand/tab_seal.png"
          alt="TAB seal"
          data-testid="tab-seal-hud"
          style={{ width: 44, height: 44, borderRadius: 999, boxShadow: "0 0 0 1px rgba(201,162,75,0.4)", flexShrink: 0 }}
        />
        <div style={{ minWidth: 0 }}>
          <div className="font-serif-q" style={{ color: "#c9a24b", fontSize: 15, letterSpacing: "0.04em", lineHeight: 1.1 }}>
            9/11: The Immaculate Salvation
          </div>
          <div className="font-serif-q hud-phase-title" style={{ fontSize: 10, color: "#7e8594", letterSpacing: "0.18em", textTransform: "uppercase", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {phaseTitle}
        </div>
        </div>
      </div>

      {/* RIGHT quadrant — timer on top, audio + controls beneath */}
      <div className="hud-right" style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 8, flexShrink: 0 }}>
        {studyMode ? (
          <div
            data-testid="hud-guided-study"
            className="hud-panel"
            style={{ padding: "8px 18px", borderRadius: 4, borderColor: "rgba(201,162,75,0.28)" }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <BookOpen size={14} color="#c9a24b" />
              <span style={{ fontSize: 9, letterSpacing: "0.24em", textTransform: "uppercase", color: "#9aa0ac" }}>Guided Study</span>
            </div>
            <div style={{ display: "flex", alignItems: "baseline", gap: 12 }}>
              <span className="font-serif-q" style={{ fontSize: 20, fontWeight: 600, color: "#f2c85b", lineHeight: 1 }}>Explore at your pace</span>
              <span className="font-mono-tel" style={{ fontSize: 11, color: "#7e8594" }}>{historicalText}</span>
            </div>
          </div>
        ) : (
          <div
            data-testid="hud-survival-timer"
            className={`hud-panel ${decay ? "decay-shake" : ""}`}
            style={{ padding: "8px 18px", borderRadius: 4, borderColor: decay ? "rgba(185,56,38,0.6)" : "rgba(201,162,75,0.28)", textAlign: "right" }}
          >
            <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 8 }}>
              {decay && <AlertTriangle size={14} color="#b93826" />}
              <span style={{ fontSize: 9, letterSpacing: "0.24em", textTransform: "uppercase", color: decay ? "#e2743a" : "#9aa0ac" }}>
                {decay ? "Structural Failure Imminent" : "Survival Window"}
              </span>
            </div>
            <div style={{ display: "flex", alignItems: "baseline", justifyContent: "flex-end", gap: 12 }}>
              <span className="font-mono-tel" style={{ fontSize: 30, fontWeight: 700, letterSpacing: "0.06em", color: decay ? "#e2743a" : "#f2c85b", lineHeight: 1 }}>
                {countdownText}
              </span>
              <span className="font-mono-tel" style={{ fontSize: 11, color: "#7e8594" }}>{historicalText}</span>
            </div>
          </div>
        )}

        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", justifyContent: "flex-end", maxWidth: "60vw" }}>
          <button
            data-testid="fact-check-open-button"
            onClick={onFactCheck}
            className="ghost-btn hud-touch tab-gold-btn hud-secondary-btn"
            style={{ display: "flex", alignItems: "center", gap: 7, padding: "8px 14px", borderRadius: 3, fontSize: 11, cursor: "pointer" }}
          >
            <BookOpen size={14} /> <span className="hud-btn-label">Fact Check</span>
          </button>

          {/* Audio Mixer — Ambient Chaos, opens directly beneath the timer (top-right), z-index 1000 */}
          <div style={{ position: "relative" }} className="hud-secondary-btn">
            <button
              data-testid="hud-audio-mixer-toggle"
              onClick={() => setMixerOpen((o) => !o)}
              className="ghost-btn hud-touch tab-gold-btn"
              style={{ display: "flex", alignItems: "center", gap: 7, padding: "8px 14px", borderRadius: 3, fontSize: 11, cursor: "pointer" }}
            >
              <Sliders size={14} /> <span className="hud-btn-label">Audio</span>
            </button>
            {mixerOpen && (
              <div
                data-testid="hud-audio-mixer"
                className="hud-panel"
                style={{ position: "absolute", top: 44, right: 0, zIndex: 1000, width: 210, padding: "12px 14px", borderRadius: 8 }}
              >
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
                  <span style={{ fontSize: 9, letterSpacing: "0.2em", textTransform: "uppercase", color: "#c9a24b" }}>Ambient Chaos</span>
                  <button
                    data-testid="hud-mixer-mute"
                    onClick={onToggleMute}
                    className="hud-touch"
                    style={{ width: 30, height: 30, borderRadius: 999, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", background: muted ? "rgba(185,56,38,0.18)" : "rgba(201,162,75,0.12)", border: `1px solid ${muted ? "#b93826" : "#c9a24b"}66`, color: muted ? "#e0533b" : "#c9a24b" }}
                  >
                    {muted ? <VolumeX size={14} /> : <Volume2 size={14} />}
                  </button>
                </div>
                <input
                  data-testid="hud-mixer-volume"
                  type="range"
                  min="0"
                  max="100"
                  value={Math.round((muted ? 0 : (volume ?? 0.9)) * 100)}
                  onChange={(e) => onVolume && onVolume(Number(e.target.value) / 100)}
                  style={{ width: "100%", accentColor: "#c9a24b", cursor: "pointer" }}
                />
                <div style={{ fontSize: 9.5, color: "#7e8594", marginTop: 4 }} className="font-mono-tel">Sirens · Roars · Groans</div>
              </div>
            )}
          </div>

          <button
            data-testid="hud-mute-toggle"
            onClick={onToggleMute}
            className="ghost-btn hud-touch"
            style={{ padding: "8px 11px", borderRadius: 3, cursor: "pointer" }}
            aria-label="Toggle audio"
          >
            {muted ? <VolumeX size={15} /> : <Volume2 size={15} />}
          </button>

          {vrSupported && (
            <button
              data-testid="webxr-enter-vr-button"
              onClick={onEnterVR}
              className="gold-btn hud-touch"
              style={{ display: "flex", alignItems: "center", gap: 7, padding: "8px 16px", borderRadius: 3, fontSize: 11, cursor: "pointer" }}
            >
              <Glasses size={14} /> <span className="hud-btn-label">Enter VR</span>
            </button>
          )}

          {/* Central user MENU — next to the audio controls */}
          <button
            data-testid="hud-menu-button"
            onClick={onMenu}
            className="ghost-btn hud-touch tab-gold-btn hud-menu-btn"
            style={{ display: "flex", alignItems: "center", gap: 7, padding: "8px 14px", borderRadius: 3, fontSize: 11, cursor: "pointer" }}
          >
            <Menu size={14} /> <span className="hud-btn-label font-serif-q" style={{ letterSpacing: "0.06em" }}>Menu</span>
          </button>
        </div>
      </div>
    </div>
  );
}
